import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Chip, CircularProgress, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  RedeemRounded, WorkspacePremiumRounded, DataUsageRounded, PaymentsRounded,
  ArrowForwardRounded, AddRounded, CreditCardRounded, WarningAmberRounded,
} from '@mui/icons-material';
import { colors, brand } from '../../theme/theme';
import { useSubscription, ActiveSubscriptionItem, ActiveSubscriptionsData, TrialStatus } from '../../hooks/useSubscription';
import { useDashboard } from '../../hooks/useDashboard';
import { rs, typeBadge, fmtDate } from './billingUtils';

const card = { bgcolor: colors.backgroundPaper, border: `1px solid ${colors.border}`, borderRadius: '14px' };

const SubscriptionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { fetchActiveSubscriptions, fetchTrialStatus, cancelSubscription } = useSubscription();
  const { tokenUsage, fetchDashboardData } = useDashboard();

  const [active, setActive] = useState<ActiveSubscriptionsData | null>(null);
  const [trial, setTrial] = useState<TrialStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<ActiveSubscriptionItem | null>(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [a, t] = await Promise.all([fetchActiveSubscriptions(), fetchTrialStatus()]);
      setActive(a); setTrial(t);
      fetchDashboardData();
    } catch (e: any) { setError(e?.response?.data?.message || 'Failed to load subscriptions.'); }
    finally { setLoading(false); }
  }, [fetchActiveSubscriptions, fetchTrialStatus, fetchDashboardData]);

  useEffect(() => { reload(); }, [reload]);

  const doCancel = async () => {
    if (!cancelTarget) return;
    const s = cancelTarget; setCancelTarget(null);
    try { await cancelSubscription(s.id); setToast('Subscription set to cancel at period end'); reload(); }
    catch (e: any) { setToast(e?.response?.data?.message || 'Failed to cancel'); }
  };

  const subs = active?.subscriptions ?? [];
  const totals = active?.totals;
  const usedTotal = tokenUsage?.subscription_token_count ?? 0;
  const remaining = tokenUsage?.remaining_tokens ?? 0;
  const used = Math.max(0, usedTotal - remaining);
  const usagePct = usedTotal ? Math.min(100, Math.round((used / usedTotal) * 100)) : 0;
  const usageColor = usagePct > 80 ? brand.capacity.high : usagePct >= 50 ? brand.capacity.medium : brand.capacity.low;

  if (loading) return <Box sx={{ display: 'grid', placeItems: 'center', py: 10 }}><CircularProgress /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Trial banner */}
      {trial?.is_on_trial && (
        <Box sx={{ ...card, p: 2.25, mb: 2.25, bgcolor: colors.primaryLight, borderColor: '#DBEAFE', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <Box sx={{ width: 46, height: 46, borderRadius: '12px', bgcolor: '#fff', color: colors.primary, display: 'grid', placeItems: 'center' }}><RedeemRounded /></Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 16 }}>Free trial active — {trial.provider_count} provider + {trial.token_count} tokens + Prescription</Typography>
              <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>
                {trial.days_remaining} days remaining{trial.trial_end_date ? ` (ends ${fmtDate(trial.trial_end_date)})` : ''}. Need more? Upgrade to a paid plan.
              </Typography>
            </Box>
          </Box>
          <Button variant="contained" endIcon={<ArrowForwardRounded />} onClick={() => navigate('/plans')}>Upgrade</Button>
        </Box>
      )}

      {/* Stat cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3,1fr)' }, gap: 2.25, mb: 2.5 }}>
        <Box sx={{ ...card, p: 2.25 }}>
          <Box sx={{ width: 42, height: 42, borderRadius: '12px', bgcolor: '#076FF722', color: '#076FF7', display: 'grid', placeItems: 'center' }}><WorkspacePremiumRounded /></Box>
          <Typography sx={{ fontSize: 28, fontWeight: 800, mt: 1.5 }}>{subs.length}</Typography>
          <Typography sx={{ color: colors.textSecondary, fontSize: 13 }}>Active plans</Typography>
          {totals && <Typography sx={{ fontSize: 12.5, color: colors.textSecondary, mt: 0.5 }}>{totals.total_providers} providers • {totals.total_tokens.toLocaleString('en-IN')} tokens</Typography>}
        </Box>
        <Box sx={{ ...card, p: 2.25 }}>
          <Box sx={{ width: 42, height: 42, borderRadius: '12px', bgcolor: '#10B98122', color: '#10B981', display: 'grid', placeItems: 'center' }}><DataUsageRounded /></Box>
          <Typography sx={{ fontSize: 28, fontWeight: 800, mt: 1.5 }}>{used}<span style={{ fontSize: 16, color: colors.textTertiary }}>/{usedTotal}</span></Typography>
          <Typography sx={{ color: colors.textSecondary, fontSize: 13 }}>Token usage</Typography>
          <Box sx={{ height: 8, borderRadius: 99, bgcolor: colors.gray100, mt: 1, overflow: 'hidden' }}><Box sx={{ height: '100%', width: `${usagePct}%`, bgcolor: usageColor }} /></Box>
          <Typography sx={{ fontSize: 12.5, color: colors.textSecondary, mt: 0.5 }}>{remaining.toLocaleString('en-IN')} tokens remaining</Typography>
        </Box>
        <Box sx={{ ...card, p: 2.25 }}>
          <Box sx={{ width: 42, height: 42, borderRadius: '12px', bgcolor: '#7C3AED22', color: '#7C3AED', display: 'grid', placeItems: 'center' }}><PaymentsRounded /></Box>
          <Typography sx={{ fontSize: 28, fontWeight: 800, mt: 1.5 }}>{rs(totals?.total_monthly_cost ?? 0)}</Typography>
          <Typography sx={{ color: colors.textSecondary, fontSize: 13 }}>Billed monthly</Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Your subscriptions</Typography>
        <Button size="small" variant="contained" startIcon={<AddRounded />} onClick={() => navigate('/plans')}>Add / upgrade plan</Button>
      </Box>

      {subs.length === 0 ? (
        <Box sx={{ ...card, textAlign: 'center', py: 8 }}>
          <Box sx={{ width: 88, height: 88, borderRadius: '24px', bgcolor: colors.primaryLight, color: colors.primary, display: 'grid', placeItems: 'center', mx: 'auto', mb: 2 }}><CreditCardRounded sx={{ fontSize: 40 }} /></Box>
          <Typography variant="h3" sx={{ mb: 0.5 }}>No active subscription</Typography>
          <Typography color="text.secondary" sx={{ mb: 2.5 }}>Purchase a plan to activate your queues and start managing tokens.</Typography>
          <Button variant="contained" onClick={() => navigate('/plans')}>Choose a plan</Button>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3,1fr)' }, gap: 2.25 }}>
          {subs.map((s) => {
            const cancelling = s.status === 'cancelling';
            const failed = s.payment_retry_count > 0;
            const tb = typeBadge(s.plan_type_category || 'bundle');
            return (
              <Box key={s.id} sx={{ ...card, p: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', gap: 0.75 }}>
                    <Chip size="small" label={tb.label} sx={{ bgcolor: tb.bg, color: tb.fg, fontWeight: 600, height: 22 }} />
                    {cancelling && <Chip size="small" label="Cancelling" sx={{ bgcolor: '#FEF2F2', color: colors.error, fontWeight: 600, height: 22 }} />}
                  </Box>
                  <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>Qty: {s.quantity}</Typography>
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: 16, mt: 1 }}>{s.plan_name || `${s.provider_count}P, ${s.token_count}T`}</Typography>
                <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>{s.provider_count} providers • {s.token_count.toLocaleString('en-IN')} tokens</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mt: 2 }}>
                  <Typography><span style={{ fontSize: 22, fontWeight: 800 }}>{rs(s.amount * s.quantity)}</span><span style={{ color: colors.textSecondary }}>/{s.plan_type === 'annual' ? 'yr' : 'mo'}</span></Typography>
                  {!cancelling && <Button size="small" onClick={() => setCancelTarget(s)} sx={{ color: colors.error, bgcolor: '#FEF2F2', '&:hover': { bgcolor: '#FEE2E2' } }}>Cancel</Button>}
                </Box>
                <Typography sx={{ fontSize: 13, color: colors.textSecondary, mt: 1 }}>{cancelling ? 'Active until' : 'Renews'} {fmtDate(s.end_date)}</Typography>
                {cancelling && <Alert severity="warning" sx={{ mt: 1.25, py: 0 }} icon={false}>Plan active until {fmtDate(s.end_date)}. No further charges.</Alert>}
                {failed && <Alert severity="error" icon={<WarningAmberRounded />} sx={{ mt: 1.25, py: 0 }}>Payment failed ({s.payment_retry_count}/3 attempts)</Alert>}
              </Box>
            );
          })}
        </Box>
      )}

      {/* Cancel dialog */}
      <Dialog open={!!cancelTarget} onClose={() => setCancelTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>Cancel subscription</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 1 }}>Are you sure you want to cancel <b>{cancelTarget?.plan_name || 'this plan'}</b>?</Typography>
          <Box component="ul" sx={{ color: colors.textSecondary, pl: 2.5, m: 0, lineHeight: 1.9, fontSize: 14 }}>
            <li>Plan remains active until the end of the billing period.</li>
            <li>No further charges will be made.</li>
            <li>Queues deactivate after the period ends if no other plans are active.</li>
            <li>This action cannot be undone.</li>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelTarget(null)}>Keep subscription</Button>
          <Button color="error" variant="contained" onClick={doCancel}>Yes, cancel</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={2800} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

export default SubscriptionsPage;
